import React, { useEffect, useRef } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

interface TrackedComponentProps {
  children: React.ReactNode;
  sectionName: string;
  trackScroll?: boolean;
  trackHover?: boolean;
  trackClick?: boolean;
  className?: string;
}

const TrackedComponent: React.FC<TrackedComponentProps> = ({
  children,
  sectionName,
  trackScroll = true,
  trackHover = false,
  trackClick = false,
  className = ''
}) => {
  const { trackSectionView, trackScrollDepth } = useAnalytics();
  const elementRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);
  const hasTrackedScroll = useRef<Set<number>>(new Set());

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Track section view when it comes into viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            trackSectionView(sectionName);
            hasTrackedView.current = true;
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of the section is visible
    );

    observer.observe(element);

    // Track scroll depth
    if (trackScroll) {
      const handleScroll = () => {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const elementTop = rect.top;
        const elementHeight = rect.height;
        
        // Calculate scroll depth percentage
        const scrollDepth = Math.max(0, Math.min(100, 
          ((windowHeight - elementTop) / elementHeight) * 100
        ));
        
        // Track at 25%, 50%, 75%, and 100% intervals
        const milestones = [25, 50, 75, 100];
        milestones.forEach(milestone => {
          if (scrollDepth >= milestone && !hasTrackedScroll.current.has(milestone)) {
            trackScrollDepth(sectionName, milestone);
            hasTrackedScroll.current.add(milestone);
          }
        });
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }

    return () => {
      observer.disconnect();
    };
  }, [sectionName, trackScroll, trackSectionView, trackScrollDepth]);

  // Track hover events
  const handleMouseEnter = () => {
    if (trackHover) {
      trackSectionView(`${sectionName} - Hover`);
    }
  };

  // Track click events
  const handleClick = () => {
    if (trackClick) {
      trackSectionView(`${sectionName} - Click`);
    }
  };

  return (
    <div
      ref={elementRef}
      className={className}
      onMouseEnter={trackHover ? handleMouseEnter : undefined}
      onClick={trackClick ? handleClick : undefined}
    >
      {children}
    </div>
  );
};

export default TrackedComponent;
